import React, { useState, useEffect, useMemo } from "react";
import dayjs from "dayjs";
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import "react-datepicker/dist/react-datepicker.css";
import { useSelector } from "react-redux";
import { Col, Form, message, Row } from "antd";
import { getOptionsByPath } from "../../utilsdata";
import { EMPLOYEE_LEAVES, LEAVE_TYPE } from "../../Dropdownmanager";
import { useDispatch } from "react-redux";
import Swal from "sweetalert2";
import { InputField, DatePickerField, SelectInput, TextareaField } from "../../blueplanitUtils/InputField";
import Modal from "../../blueplanitUtils/Modal";
import { createLeaveRequestThunk, getLeaveRequestsThunk, setAllLeaves, updateLeaveStatusThunk } from "../../Slices/LeavesSlice";
import { fetchAdminLeaves } from "../../Slices/adminLeaveSlice";
import { handleKeyDown } from "../../blueplanitUtils/DatePickerAutoformate";
import { fetchHolidays } from "../../Slices/holidaySlice";

dayjs.extend(utc);
dayjs.extend(timezone);

const isSessionTaken = (val) => val === null || val === "true";

const getQuarter = (dateStr) => {
  const month = new Date(dateStr).getMonth() + 1;
  return Math.ceil(month / 3);
};

const getMonthInQuarter = (dateStr) => {
  const month = new Date(dateStr).getMonth();
  return (month % 3) + 1;
};

const computeCLWarning = ({ allLeaves, activeEmployeeId, startDate, requestedDays, yearlyCL }) => {
  if (!startDate || !requestedDays || !yearlyCL) return null;
  const year = new Date(startDate).getFullYear().toString();
  const quarter = getQuarter(startDate);
  const monthInQuarter = getMonthInQuarter(startDate);
  const approvedCLLeaves = (allLeaves || []).filter((l) => {
    if (l.leaveStatus !== "Approved") return false;
    if (l.leaveType !== "Casual Leave") return false;
    if (l.employeeId !== activeEmployeeId) return false;
    return new Date(l.startDate).getFullYear().toString() === year;
  });
  const usedCL = approvedCLLeaves.reduce((sum, l) => sum + Number(l.approvedDays || 0), 0);
  const quarterlyMax = Math.floor(yearlyCL / 4);
  const allowedTillPrevQuarter = Math.floor((yearlyCL * (quarter - 1)) / 4);
  const carriedForwardCL = Math.max(0, allowedTillPrevQuarter - usedCL);
  let allowedThisQuarter;
  if (monthInQuarter === 1) {
    allowedThisQuarter = 2 + carriedForwardCL;
  } else {
    allowedThisQuarter = quarterlyMax + carriedForwardCL;
  }
  const alreadyUsedThisQuarter = Math.max(0, usedCL - allowedTillPrevQuarter);
  const remainingYearlyCL = Math.max(0, yearlyCL - usedCL);
  const availableCL = Math.min(Math.max(0, allowedThisQuarter - alreadyUsedThisQuarter), remainingYearlyCL);
  const eligible = Math.min(requestedDays, availableCL);
  const lop = requestedDays - eligible;
  if (lop <= 0) return null;
  if (monthInQuarter === 1) {
    return {
      type: "month", eligible, lop,
      message: `Month 1 limit is ${allowedThisQuarter} CL (2 base${carriedForwardCL > 0 ? ` + ${carriedForwardCL} carry forward` : ""}). You requested ${requestedDays} day(s) — ${eligible} will be approved as CL, ${lop} day(s) will be LOP.`,
    };
  } else {
    return {
      type: "quarter", eligible, lop,
      message: `Quarterly limit is ${allowedThisQuarter} CL (${quarterlyMax} base${carriedForwardCL > 0 ? ` + ${carriedForwardCL} carry forward` : ""}). You requested ${requestedDays} day(s) — ${eligible} will be approved as CL, ${lop} day(s) will be LOP.`,
    };
  }
};

const computeSLWarning = ({ allLeaves, activeEmployeeId, startDate, requestedDays, yearlySL }) => {
  if (!startDate || !requestedDays || !yearlySL) return null;
  const year = new Date(startDate).getFullYear().toString();
  const approvedSLLeaves = (allLeaves || []).filter((l) => {
    if (l.leaveStatus !== "Approved") return false;
    if (l.leaveType !== "Sick Leave") return false;
    if (l.employeeId !== activeEmployeeId) return false;
    return new Date(l.startDate).getFullYear().toString() === year;
  });
  const usedSL = approvedSLLeaves.reduce((sum, l) => sum + Number(l.approvedDays || 0), 0);
  const remainingSL = Math.max(0, yearlySL - usedSL);
  const eligible = Math.min(requestedDays, remainingSL);
  const lop = requestedDays - eligible;
  if (lop <= 0) return null;
  return {
    type: "sick", eligible, lop,
    message: `Yearly SL limit is ${yearlySL} days. You've used ${usedSL} day(s) — ${eligible} will be approved as SL, ${lop} day(s) will be LOP.`,
  };
};

const computeMaternityWarning = ({ requestedDays }) => {
  const paidLimit = 30;
  if (!requestedDays || requestedDays <= paidLimit) return null;
  const lop = requestedDays - paidLimit;
  return {
    type: "maternity", eligible: paidLimit, lop,
    message: `Blueplanit pays for ${paidLimit} days of Maternity Leave. You requested ${requestedDays} day(s) — ${paidLimit} will be paid, ${lop} day(s) will be Loss of Pay.`,
  };
};

const computePaternityWarning = ({ requestedDays }) => {
  const paidLimit = 3;
  if (!requestedDays || requestedDays <= paidLimit) return null;
  const lop = requestedDays - paidLimit;
  return {
    type: "paternity", eligible: paidLimit, lop,
    message: `Blueplanit pays for ${paidLimit} days of Paternity Leave. You requested ${requestedDays} day(s) — ${paidLimit} will be paid, ${lop} day(s) will be Loss of Pay.`,
  };
};

const LeaveWarningBanner = ({ warning }) => {
  if (!warning) return null;
  const isMonth = warning.type === "month";
  const isQuarter = warning.type === "quarter";
  const isMaternity = warning.type === "maternity";
  const isPaternity = warning.type === "paternity";
  const bgColor = isMonth ? "#fffbe6" : isQuarter ? "#fff1f0" : isMaternity ? "#f9f0ff" : isPaternity ? "#e6f7ff" : "#f0f9ff";
  const borderColor = isMonth ? "#ffe58f" : isQuarter ? "#ffa39e" : isMaternity ? "#d3adf7" : isPaternity ? "#91d5ff" : "#91d5ff";
  const titleColor = isMonth ? "#d46b08" : isQuarter ? "#cf1322" : isMaternity ? "#531dab" : isPaternity ? "#096dd9" : "#096dd9";
  const msgColor = isMonth ? "#874d00" : isQuarter ? "#820014" : isMaternity ? "#22075e" : isPaternity ? "#003a8c" : "#003a8c";
  const styles = {
    wrapper: { display: "flex", alignItems: "flex-start", gap: "10px", padding: "10px 14px", borderRadius: "8px", marginBottom: "12px", marginTop: "-8px", backgroundColor: bgColor, border: `1px solid ${borderColor}` },
    icon: { fontSize: "16px", marginTop: "1px", flexShrink: 0 },
    content: { flex: 1 },
    title: { fontWeight: 600, fontSize: "13px", marginBottom: "2px", color: titleColor },
    message: { fontSize: "12px", color: msgColor, lineHeight: "1.5" },
    pills: { display: "flex", gap: "8px", marginTop: "6px" },
    pillApproved: { fontSize: "11px", padding: "2px 8px", borderRadius: "12px", backgroundColor: "#f6ffed", border: "1px solid #b7eb8f", color: "#389e0d", fontWeight: 600 },
    pillLOP: { fontSize: "11px", padding: "2px 8px", borderRadius: "12px", backgroundColor: "#fff2f0", border: "1px solid #ffccc7", color: "#cf1322", fontWeight: 600 },
  };
  const icon = isMonth ? "⚠️" : isQuarter ? "🚫" : isMaternity ? "⚠️" : isPaternity ? "⚠️" : "🏥";
  const title = isMonth ? "Monthly Limit Exceeded" : isQuarter ? "Quarterly Limit Exceeded" : isMaternity ? "Maternity Leave Limit Exceeded" : isPaternity ? "Paternity Leave Limit Exceeded" : "Sick Leave Limit Exceeded";
  return (
    <div style={styles.wrapper}>
      <span style={styles.icon}>{icon}</span>
      <div style={styles.content}>
        <div style={styles.title}>{title}</div>
        <div style={styles.message}>{warning.message}</div>
        <div style={styles.pills}>
          <span style={styles.pillApproved}>✓ {warning.eligible} day(s) Paid</span>
          <span style={styles.pillLOP}>✗ {warning.lop} day(s) as LOP</span>
        </div>
      </div>
    </div>
  );
};

const buildApprovedDateMap = ({ allLeaves, activeEmployeeId }) => {
  const dateMap = new Map();

  (allLeaves || []).forEach((leave) => {
    if (leave.leaveStatus !== "Approved") return;
    if (leave.employeeId !== activeEmployeeId) return;

    const s1 = isSessionTaken(leave.session1);
    const s2 = isSessionTaken(leave.session2);

    // Skip records where neither session is taken (invalid data)
    if (!s1 && !s2) return;

    const start = dayjs(leave.startDate, "YYYY-MM-DD").startOf("day");
    const end = dayjs(leave.endDate, "YYYY-MM-DD").startOf("day");
    const isSingleDay = start.isSame(end, "day");

    let current = start;
    while (current.isBefore(end) || current.isSame(end, "day")) {
      const dateStr = current.format("YYYY-MM-DD");
      const existing = dateMap.get(dateStr) || { s1: false, s2: false };

      let dayS1, dayS2;

      if (isSingleDay) {
        // Single day: use exact session values
        dayS1 = s1;
        dayS2 = s2;
      } else if (current.isSame(start, "day")) {
        // First day: s1 from record, s2 forced true (rest of day is full)
        dayS1 = s1;
        dayS2 = true;
      } else if (current.isSame(end, "day")) {
        // Last day: s1 forced true, s2 from record
        dayS1 = true;
        dayS2 = s2;
      } else {
        // Middle days: always fully taken
        dayS1 = true;
        dayS2 = true;
      }

      dateMap.set(dateStr, {
        s1: existing.s1 || dayS1,
        s2: existing.s2 || dayS2,
      });

      current = current.add(1, "day");
    }
  });

  return dateMap;
};

const countWorkingDays = ({ fromDate, fromSession, toDate, toSession, holidayDates, is5Day, approvedDateMap }) => {
  const start = dayjs(fromDate).startOf("day");
  const end = dayjs(toDate).startOf("day");

  const getApproved = (d) => approvedDateMap?.get(d.format("YYYY-MM-DD")) || { s1: false, s2: false };

  const isNonWorkingDay = (d) => {
    const dow = d.day();
    const isWeekend = is5Day ? (dow === 0 || dow === 6) : dow === 0;
    const isHoliday = holidayDates.has(d.format("YYYY-MM-DD"));
    const approved = getApproved(d);
    return isWeekend || isHoliday || (approved.s1 && approved.s2);
  };

  // ── Same day ──
  if (start.isSame(end, "day")) {
    if (isNonWorkingDay(start)) return 0;
    const approved = getApproved(start);
    if (fromSession === "session1" && toSession === "session1") {
      return approved.s1 ? 0 : 0.5;
    } else if (fromSession === "session2" && toSession === "session2") {
      return approved.s2 ? 0 : 0.5;
    } else {
      if (approved.s1 && !approved.s2) return 0.5;
      if (!approved.s1 && approved.s2) return 0.5;
      return 1;
    }
  }

  // ── Multiple days ──
  let total = 0;
  let current = start;
  while (current.isBefore(end) || current.isSame(end, "day")) {
    if (!isNonWorkingDay(current)) {
      const isFirstDay = current.isSame(start, "day");
      const isLastDay = current.isSame(end, "day");
      const approved = getApproved(current);
      let dayValue = 0;

      if (isFirstDay) {
        if (fromSession === "session2") {
          dayValue = approved.s2 ? 0 : 0.5;
        } else {
          if (approved.s1 && !approved.s2) dayValue = 0.5;
          else dayValue = 1;
        }
      } else if (isLastDay) {
        if (toSession === "session1") {
          dayValue = approved.s1 ? 0 : 0.5;
        } else {
          if (!approved.s1 && approved.s2) dayValue = 0.5;
          else dayValue = 1;
        }
      } else {
        dayValue = 1; // middle days always full (fully approved already skipped)
      }

      total += dayValue;
    }
    current = current.add(1, "day");
  }
  return total;
};

const EmployeeLeaveModelPopup = (props) => {
  const { leaveData, isOpen, onClose, allLeaves, appliedByAdmin, remainingLeaves, employeeList } = props;

  const dispatch = useDispatch();
  const loginUser = useSelector((state) => state?.loginUserInfo?.loginUserDetails);
  const NestedDatabucket = useSelector((state) => state.dataBucket.NestedDatabucket);
  const allHolidays = useSelector((state) => state.holidayData?.holiday) || [];

  const [formData, setFormData] = useState({
    employeeId: "", leaveType: null, fromDate: null, toDate: null,
    numberOfDays: 0, leaveReason: "", fromSession: "", toSession: "",
  });
  const [isModified, setIsModified] = useState(false);
  const [leaveWarning, setLeaveWarning] = useState(null);

  // ── SINGLE SOURCE OF TRUTH: resolve which employee's context to use ──
  // If appliedByAdmin → selected employee from employeeList
  // Else             → the logged-in user
  const activeEmployee = useMemo(() => {
    if (appliedByAdmin) {
      return employeeList?.find((e) => e.employeeId === formData.employeeId) || null;
    }
    return loginUser || null;
  }, [appliedByAdmin, formData.employeeId, employeeList, loginUser]);
  console.log("Active Employee:",employeeList, activeEmployee);
  const activeEmployeeId = appliedByAdmin ? formData.employeeId : loginUser?.id;
  const activeDepartment = activeEmployee?.department || null;

  const currentYear = new Date().getFullYear();

  // Leave settings always from the active employee, not loginUser
  const employeeLeavesData = useMemo(() => {
    if (!activeEmployee) return {};
    return (
      activeEmployee?.leavesSettings?.find(
        (s) => s.year === String(currentYear) && s.department === activeEmployee?.department
      ) || {}
    );
  }, [activeEmployee, currentYear]);

  const totalCasualLeaves = Number(employeeLeavesData?.casualLeaves || 0);
  const totalMedicalLeaves = Number(employeeLeavesData?.medicalLeave || 0);
  const is5Day = totalCasualLeaves === 12;

  // Holiday dates always scoped to the active employee's department
  const holidayDates = useMemo(() => new Set(
    allHolidays
      .filter((h) => h.department === activeDepartment && h.type === "Mandatory")
      .map((h) => dayjs(h.date).format("YYYY-MM-DD"))
  ), [allHolidays, activeDepartment]);

  // ── Approved date map — scoped to active employee ──
  const approvedDateMap = useMemo(
    () => buildApprovedDateMap({ allLeaves, activeEmployeeId }),
    [allLeaves, activeEmployeeId]
  );

  // Fetch holidays for the active employee's department whenever it changes
  useEffect(() => {
    if (activeDepartment) {
      dispatch(fetchHolidays({ department: activeDepartment, role: loginUser?.role }));
    }
  }, [dispatch, activeDepartment]);

  useEffect(() => {
    if (leaveData) {
      setFormData({
        employeeId: leaveData.employeeId || "",
        leaveType: leaveData.leaveType || null,
        fromDate: leaveData.startDate ? dayjs(leaveData.startDate) : null,
        toDate: leaveData.endDate ? dayjs(leaveData.endDate) : null,
        leaveReason: leaveData.leaveReason || "",
        numberOfDays: leaveData.noOfDays || 0,
        fromSession:
          leaveData?.session1 === "true" ? "session1"
            : leaveData?.session1 === "false" ? "session2"
              : null,
        toSession:
          leaveData?.session2 === "true" ? "session2"
            : leaveData?.session2 === "false" ? "session1"
              : null,
      });
      setIsModified(false);
    }
  }, [leaveData]);

  // ── Real-time warning ──
  useEffect(() => {
    const { leaveType, fromDate, numberOfDays } = formData;
    if (!leaveType || !fromDate || !numberOfDays || numberOfDays <= 0) { setLeaveWarning(null); return; }
    if (!activeEmployeeId) { setLeaveWarning(null); return; }
    const startDate = dayjs(fromDate).format("YYYY-MM-DD");
    const requested = parseFloat(numberOfDays);
    if (leaveType === "Casual Leave") {
      setLeaveWarning(computeCLWarning({ allLeaves, activeEmployeeId, startDate, requestedDays: requested, yearlyCL: totalCasualLeaves }));
    } else if (leaveType === "Sick Leave") {
      setLeaveWarning(computeSLWarning({ allLeaves, activeEmployeeId, startDate, requestedDays: requested, yearlySL: totalMedicalLeaves }));
    } else if (leaveType === "Maternity Leave") {
      setLeaveWarning(computeMaternityWarning({ requestedDays: requested }));
    } else if (leaveType === "Paternity Leave") {
      setLeaveWarning(computePaternityWarning({ requestedDays: requested }));
    } else {
      setLeaveWarning(null);
    }
  }, [formData.numberOfDays, formData.leaveType, formData.fromDate, formData.employeeId]);

  // ── Calculate working days (uses memoized approvedDateMap + active employee's holidays) ──
  const calculateDays = (fromDate, fromSession, toDate, toSession) => {
    if (!fromDate || !toDate) return 0;
    return countWorkingDays({
      fromDate,
      fromSession: fromSession || null,
      toDate,
      toSession: toSession || null,
      holidayDates,
      is5Day,
      approvedDateMap,
    });
  };

  // ── Disable weekends + holidays (active employee dept) + fully-approved dates ──
  const disableLeaveDates = (current) => {
    if (!current) return false;
    const dow = current.day();
    const isWeekend = is5Day ? (dow === 0 || dow === 6) : dow === 0;
    const isHoliday = holidayDates.has(dayjs(current).format("YYYY-MM-DD"));
    if (isWeekend || isHoliday) return true;
    if (!allLeaves || !activeEmployeeId) return false;
    const approved = approvedDateMap.get(dayjs(current).format("YYYY-MM-DD"));
    // Disable only if BOTH sessions are taken
    return approved?.s1 === true && approved?.s2 === true;
  };

  const handleChange = (name, value) => {
    setFormData((prevData) => {
      let updatedData = { ...prevData, [name]: value };
      if (["fromDate", "toDate"].includes(name) && value) updatedData[name] = dayjs(value);
      if (["fromDate", "toDate", "fromSession", "toSession"].includes(name)) {
        updatedData.numberOfDays = calculateDays(updatedData.fromDate, updatedData.fromSession, updatedData.toDate, updatedData.toSession);
      }
      if (name === "fromDate") updatedData.fromSession = "";
      if (name === "toDate") updatedData.toSession = "";
      if (leaveData) setIsModified(true);
      return updatedData;
    });
  };

  const [isDisabled, setIsDisabled] = useState(false);
  const startTimer = () => { setIsDisabled(true); setTimeout(() => setIsDisabled(false), 5000); };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.leaveType || !formData.fromDate || !formData.toDate || !formData.leaveReason) {
      startTimer(); message.error("Please fill all required fields!"); return;
    }
    if (leaveData && !isModified) {
      startTimer(); message.error("You haven't updated anything. Please make changes before submitting."); return;
    }
    const fromDate = formData.fromDate instanceof Date ? formData.fromDate : formData.fromDate.toDate();
    const toDate = formData.toDate instanceof Date ? formData.toDate : formData.toDate.toDate();
    if (fromDate && toDate && fromDate.getTime() === toDate.getTime()) {
      if (formData.fromSession === "session2" && formData.toSession === "session1") {
        startTimer(); message.error("You cannot select 'Session 2' for 'From Date' and 'Session 1' for 'To Date' on the same day!"); return;
      }
    }
    if (formData.numberOfDays === 0) {
      startTimer(); message.error("Selected dates fall entirely on weekends or holidays. Please choose working days."); return;
    }

    const { numberOfDays } = formData;

    if (leaveWarning) {
      const isMonth = leaveWarning.type === "month";
      const isQuarter = leaveWarning.type === "quarter";
      Swal.fire({
        icon: "warning",
        title: isMonth ? "Monthly CL Limit Exceeded"
          : isQuarter ? "Quarterly CL Limit Exceeded"
            : leaveWarning.type === "maternity" ? "Maternity Leave Limit Exceeded"
              : leaveWarning.type === "paternity" ? "Paternity Leave Limit Exceeded"
                : "Yearly SL Limit Exceeded",
        html: `<p style="margin-bottom:12px">${leaveWarning.message}</p>
          <div style="display:flex;gap:8px;justify-content:center">
            <span style="background:#f6ffed;border:1px solid #b7eb8f;color:#389e0d;padding:4px 12px;border-radius:12px;font-weight:600">✓ ${leaveWarning.eligible} day(s) as Leave</span>
            <span style="background:#fff2f0;border:1px solid #ffccc7;color:#cf1322;padding:4px 12px;border-radius:12px;font-weight:600">✗ ${leaveWarning.lop} day(s) as LOP</span>
          </div>`,
        showCancelButton: true, confirmButtonText: "Proceed with LOP", cancelButtonText: "Cancel", confirmButtonColor: "#ff4d4f",
      }).then((result) => { if (result.isConfirmed) processLeaveRequest(); else message.info("Leave request cancelled."); });
      return;
    }

    if (parseFloat(numberOfDays) > parseFloat(remainingLeaves)) {
      Swal.fire({
        icon: "info", title: "Leave Balance Exceeded",
        text: `You have only ${remainingLeaves} leave days left. The extra ${numberOfDays - remainingLeaves} days will be considered as Pay Leave.`,
        showCancelButton: true, confirmButtonText: "Proceed", cancelButtonText: "Cancel",
      }).then((result) => { if (result.isConfirmed) processLeaveRequest(); else message.info("Leave request cancelled."); });
    } else {
      processLeaveRequest();
      dispatch(setAllLeaves());
    }
  };

  const teamReports = loginUser?.teamsReportDirect?.map((item) => item.reportingId) || [];

  const processLeaveRequest = async () => {
    const { leaveType, fromDate, toDate, leaveReason, numberOfDays, fromSession, toSession } = formData;
    const istStartDate = fromDate ? dayjs(fromDate).tz("Asia/Kolkata").valueOf().toString() : null;
    const istEndDate = toDate ? dayjs(toDate).tz("Asia/Kolkata").valueOf().toString() : null;

    // null = no session selected (full day), "true"/"false" = explicitly set
    const session1Val = fromSession ? (fromSession === "session1" ? "true" : "false") : null;
    const session2Val = toSession ? (toSession === "session2" ? "true" : "false") : null;

    const formattedData = {
      employeeId: activeEmployeeId,
      leaveType,
      startDate: dayjs(parseInt(istStartDate)).format("YYYY-MM-DD"),
      endDate: dayjs(parseInt(istEndDate)).format("YYYY-MM-DD"),
      leaveReason,
      leaveDays: numberOfDays.toString(),
      session1: session1Val,
      session2: session2Val,
      appliedByAdmin: appliedByAdmin ? "true" : "false",
      approvedBy: appliedByAdmin ? `${loginUser.firstname} ${loginUser.lastname}` : null,
    };

    if (leaveData) {
      const isDifferentLeave =
        istStartDate !== (leaveData.startDate ? parseInt(leaveData.startDate) : null) ||
        istEndDate !== (leaveData.endDate ? parseInt(leaveData.endDate) : null) ||
        session1Val !== leaveData.session1 ||
        session2Val !== leaveData.session2;
      if (!isDifferentLeave) {
        startTimer();
        message.error("You haven't updated anything. Please make date and session changes before submitting.");
        return;
      }
      await dispatch(updateLeaveStatusThunk([{
        leaveId: leaveData.leaveId,
        newStatus: leaveData.leaveStatus,
        newStartDate: dayjs(parseInt(istStartDate)).format("YYYY-MM-DD"),
        newEndDate: dayjs(parseInt(istEndDate)).format("YYYY-MM-DD"),
        leaveDays: numberOfDays.toString(),
        session1: session1Val,
        session2: session2Val,
      }]));
      // Refresh leaves for the correct employee, not the admin
      dispatch(getLeaveRequestsThunk({ employeeId: activeEmployeeId }));
    } else {
      dispatch(createLeaveRequestThunk(formattedData)).unwrap().then(() => {
        message.success("Leave created successfully!");
        // Refresh leaves for the correct employee, not the admin
        dispatch(getLeaveRequestsThunk({ employeeId: activeEmployeeId }));
        dispatch(fetchAdminLeaves({ empid: teamReports }));
      });
    }
    document.querySelector("#add_leave .btn-close")?.click();
  };

  // ── Session availability — derived from memoized approvedDateMap (active employee) ──
  const getSessionAvailability = (date) => {
    if (!date) return { session1: true, session2: true };
    const approved = approvedDateMap.get(dayjs(date).format("YYYY-MM-DD")) || { s1: false, s2: false };
    return { session1: !approved.s1, session2: !approved.s2 };
  };

  const sessionAvailabilityFrom = formData.fromDate ? getSessionAvailability(formData.fromDate) : { session1: true, session2: true };
  const sessionAvailabilityTo = formData.toDate ? getSessionAvailability(formData.toDate) : { session1: true, session2: true };

  const resetForm = () => {
    setFormData({ leaveType: null, fromDate: null, toDate: null, numberOfDays: 0, leaveReason: "", fromSession: "", toSession: "" });
    setLeaveWarning(null);
    onClose();
  };

  const customStyles = {
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isFocused ? "#ff9b44" : "#fff",
      color: state.isFocused ? "#fff" : "#000",
      "&:hover": { backgroundColor: "#ff9b44" },
    }),
  };

  return (
    <Modal
      id="add_leave"
      title={leaveData ? "Update Leave" : "Add Leave"}
      isOpen={`${isOpen ? "show" : ""}`}
      style={{ display: isOpen ? "block" : "none" }}
      handleModalClose={resetForm}
      handleSubmit={handleSubmit}
      size="md"
      disabled={leaveData ? !isModified || isDisabled : isDisabled}
    >
      {appliedByAdmin && (
        <Row gutter={24}><Col span={24}>
          <Form.Item label="Employee Name" labelCol={{ span: 24 }}>
            <SelectInput
              value={formData.employeeId}
              onChange={(value) => handleChange("employeeId", value)}
              placeholder="Select Employee"
              options={employeeList?.map((emp) => ({ label: emp.username, value: emp.employeeId }))}
              showSearch
              filterOption={(input, option) => option.label.toLowerCase().includes(input.toLowerCase())}
            />
          </Form.Item>
        </Col></Row>
      )}

      <Row gutter={24}><Col span={24}>
        <Form.Item labelCol={{ span: 24 }} style={{ marginBottom: 2 }}>
          <SelectInput
            label="Leave Type" value={formData.leaveType}
            options={getOptionsByPath(NestedDatabucket, [EMPLOYEE_LEAVES, LEAVE_TYPE])}
            placeholder="Select Leave Type" styles={customStyles}
            onChange={(selectedValue) => handleChange("leaveType", selectedValue)}
            disabled={!!leaveData}
          />
        </Form.Item>
      </Col></Row>

      <Row gutter={24}>
        <Col span={12}>
          <Form.Item>
            <DatePickerField label="From Date" value={formData.fromDate}
              onChange={(date) => handleChange("fromDate", date)}
              format="DD/MM/YYYY" allowClear={false}
              disabledDate={appliedByAdmin ? () => false : disableLeaveDates}
              minDate={appliedByAdmin ? null : dayjs()}
              onKeyDown={handleKeyDown}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item labelCol={{ span: 24 }}>
            <SelectInput label="from Session" placeholder={"Select Session"} value={formData.fromSession}
              onChange={(value) => handleChange("fromSession", value)}
              options={[
                { label: "Session 1", value: "session1", disabled: appliedByAdmin ? false : !sessionAvailabilityFrom.session1 },
                { label: "Session 2", value: "session2", disabled: appliedByAdmin ? false : !sessionAvailabilityFrom.session2 },
              ]}
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={24}>
        <Col span={12}>
          <Form.Item labelCol={{ span: 24 }} style={{ marginTop: -20 }}>
            <DatePickerField label="To Date" value={formData.toDate}
              onChange={(date) => handleChange("toDate", date)}
              format="DD/MM/YYYY" allowClear={false}
              minDate={appliedByAdmin ? null : dayjs()}
              disabledDate={appliedByAdmin ? () => false : disableLeaveDates}
              onKeyDown={handleKeyDown}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item labelCol={{ span: 24 }} style={{ marginTop: -20 }}>
            <SelectInput label="to Session" placeholder={"Select Session"} value={formData.toSession}
              onChange={(value) => handleChange("toSession", value)}
              options={[
                { label: "Session 1", value: "session1", disabled: appliedByAdmin ? false : !sessionAvailabilityTo.session1 },
                { label: "Session 2", value: "session2", disabled: appliedByAdmin ? false : !sessionAvailabilityTo.session2 },
              ]}
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={24}><Col span={24}>
        <Form.Item labelCol={{ span: 24 }} style={{ marginTop: -20 }}>
          <InputField label="Number of Days" type="text" className="form-control" value={formData.numberOfDays} readOnly />
        </Form.Item>
      </Col></Row>

      {leaveWarning && (
        <Row gutter={24}><Col span={24}>
          <LeaveWarningBanner warning={leaveWarning} />
        </Col></Row>
      )}

      <Row gutter={24}><Col span={24}>
        <Form.Item labelCol={{ span: 24 }}>
          <TextareaField label="Leave Reason" placeholder="Enter Leave Reason" rows="4"
            value={formData.leaveReason}
            onChange={(e) => handleChange("leaveReason", e.target.value)}
            disabled={!!leaveData}
          />
        </Form.Item>
      </Col></Row>
    </Modal>
  );
};

export default EmployeeLeaveModelPopup;